import { useEffect, useMemo, useState } from "react";
import { ApiError } from "@/api/client";
import { useCreateUser, useDeleteUser, useUpdateUser, useUsers } from "@/api/users";
import type { ManagedUser } from "@/api/types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

type UserDraft = {
  role: "admin" | "user";
  active: boolean;
  password: string;
};

function toDraft(user: ManagedUser): UserDraft {
  return {
    role: user.role,
    active: user.active,
    password: "",
  };
}

export function AdminUsersPanel() {
  const users = useUsers();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  const [createUsername, setCreateUsername] = useState("");
  const [createPassword, setCreatePassword] = useState("");
  const [createRole, setCreateRole] = useState<"admin" | "user">("user");
  const [createActive, setCreateActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, UserDraft>>({});

  useEffect(() => {
    const next: Record<string, UserDraft> = {};
    for (const user of users.data ?? []) {
      next[user.username] = drafts[user.username] ?? toDraft(user);
    }
    setDrafts(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users.data]);

  const rows = users.data ?? [];
  const sortedRows = useMemo(() => [...rows].sort((a, b) => a.username.localeCompare(b.username)), [rows]);

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await createUser.mutateAsync({
        username: createUsername.trim(),
        password: createPassword,
        role: createRole,
        active: createActive,
      });
      setCreateUsername("");
      setCreatePassword("");
      setCreateRole("user");
      setCreateActive(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function onSave(user: ManagedUser) {
    setError(null);
    const draft = drafts[user.username] ?? toDraft(user);
    const body: { password?: string; role?: "admin" | "user"; active?: boolean } = {};
    if (draft.password.trim()) {
      body.password = draft.password;
    }
    if (draft.role !== user.role) {
      body.role = draft.role;
    }
    if (draft.active !== user.active) {
      body.active = draft.active;
    }
    if (Object.keys(body).length === 0) {
      return;
    }
    try {
      await updateUser.mutateAsync({ username: user.username, body });
      setDrafts((prev) => ({
        ...prev,
        [user.username]: {
          ...prev[user.username],
          password: "",
        },
      }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function onDelete(username: string) {
    setError(null);
    if (!confirm(`Delete user ${username}?`)) {
      return;
    }
    try {
      await deleteUser.mutateAsync(username);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  return (
    <div className="space-y-4">
      <form className="grid gap-3 md:grid-cols-[1fr_1fr_10rem_7rem_auto]" onSubmit={onCreate}>
        <Input
          placeholder="username"
          value={createUsername}
          onChange={(e) => setCreateUsername(e.target.value)}
          required
        />
        <Input
          placeholder="temporary password"
          type="password"
          value={createPassword}
          onChange={(e) => setCreatePassword(e.target.value)}
          required
        />
        <Select value={createRole} onValueChange={(v) => setCreateRole(v as "admin" | "user")}>
          <SelectTrigger>
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="user">user</SelectItem>
            <SelectItem value="admin">admin</SelectItem>
          </SelectContent>
        </Select>
        <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          <Switch checked={createActive} onCheckedChange={setCreateActive} />
          Active
        </label>
        <Button type="submit" disabled={createUser.isPending}>Create</Button>
      </form>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        {users.isLoading && <p className="text-sm text-muted-foreground">Loading users…</p>}
        {!users.isLoading && sortedRows.length === 0 && (
          <p className="text-sm text-muted-foreground">No users configured.</p>
        )}

        {sortedRows.map((user) => {
          const draft = drafts[user.username] ?? toDraft(user);
          return (
            <div key={user.username} className="grid gap-3 items-center rounded-md border border-border p-3 md:grid-cols-[1fr_8rem_8rem_1fr_auto_auto]">
              <div>
                <p className="text-sm font-medium text-foreground">{user.username}</p>
                <p className="text-xs text-muted-foreground">
                  Updated {new Date(user.updated_at).toLocaleString()}
                </p>
              </div>
              <Select
                value={draft.role}
                onValueChange={(v) =>
                  setDrafts((prev) => ({
                    ...prev,
                    [user.username]: { ...draft, role: v as "admin" | "user" },
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">user</SelectItem>
                  <SelectItem value="admin">admin</SelectItem>
                </SelectContent>
              </Select>
              <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                <Switch
                  checked={draft.active}
                  onCheckedChange={(checked) =>
                    setDrafts((prev) => ({
                      ...prev,
                      [user.username]: { ...draft, active: checked },
                    }))
                  }
                />
                Active
              </label>
              <Input
                placeholder="new password (optional)"
                type="password"
                value={draft.password}
                onChange={(e) =>
                  setDrafts((prev) => ({
                    ...prev,
                    [user.username]: { ...draft, password: e.target.value },
                  }))
                }
              />
              <Button type="button" variant="outline" onClick={() => onSave(user)}>
                Save
              </Button>
              <Button type="button" variant="ghost" className="text-destructive" onClick={() => onDelete(user.username)}>
                Delete
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
